"use client";

import type { PostHog } from "posthog-js";
import { useEffect, useRef, useState } from "react";
import { getPostHogEnv, isPostHogEnabled } from "./env";

/**
 * Initialises PostHog and wraps the component tree with the PostHog React
 * context so downstream components can use `usePostHog()` and friends.
 *
 * Gated to staging / preview environments — see `isPostHogEnabled()` in
 * `./env.ts` for the full decision logic.  When disabled the provider still
 * renders its children (no-op wrapper) so the rest of the app is unaffected.
 *
 * The PostHog instance is scoped to the component lifecycle via a ref. On
 * unmount (e.g. HMR, tests) `posthog.reset()` is called to clean up
 * autocapture, session recording, and internal state.
 */
export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [enabled] = useState(() => isPostHogEnabled());
  const [PHProvider, setPHProvider] = useState<React.ComponentType<{ client: PostHog; children: React.ReactNode }> | null>(null);
  const instanceRef = useRef<PostHog | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const env = getPostHogEnv();
    if (!env) return;

    let cancelled = false;
    (async () => {
      const { default: posthog } = await import("posthog-js");
      if (cancelled) return;
      posthog.init(env.key, {
        api_host: env.host,
        capture_pageview: "history_change",
        capture_pageleave: "if_capture_pageview",
        autocapture: {
          css_selector_allowlist: ["[ph-autocapture]"],
        },
        capture_exceptions: {
          capture_unhandled_errors: true,
          capture_unhandled_rejections: true,
          capture_console_errors: false,
        },
        disable_session_recording: false,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: "*",
        },
        capture_performance: {
          web_vitals: true,
          network_timing: true,
        },
        persistence: "localStorage+cookie",
        person_profiles: "identified_only",
        loaded: (ph) => {
          if (process.env.NODE_ENV === "development") ph.debug();
        },
      });
      // WIT-1237: stamp EVERY event (web vitals, pageviews, autocapture —
      // not just interaction_timing) with the deploy SHA as a super property
      // so any PostHog insight can be release-annotated and a regression can
      // be attributed to a deploy. Matches the per-event `release` property
      // use-interaction-timing.ts already sends. Web-vitals capture itself is
      // already enabled above via `capture_performance.web_vitals`.
      posthog.register({
        release: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? "dev",
      });
      instanceRef.current = posthog;

      const { PostHogProvider: Provider } = await import("posthog-js/react");
      if (cancelled) return;
      setPHProvider(() => Provider);
    })();

    return () => {
      cancelled = true;
      if (instanceRef.current) {
        // Clean up PostHog on unmount (HMR, tests) to stop autocapture,
        // session recording, and prevent stale instances on remount.
        instanceRef.current.reset();
        instanceRef.current = null;
      }
    };
  }, [enabled]);

  if (!enabled || !PHProvider || !instanceRef.current) {
    return <>{children}</>;
  }
  return <PHProvider client={instanceRef.current}>{children}</PHProvider>;
}
